import { API } from "apis";
import PageTitle from "components/common/PageTitle";
import ApplyCorpForms from "components/corp/apply/ApplyCorpForms";
import CorpInfoForms from "components/corp/info/CorpInfoForms";
import CorpInfoFormsConfirm from "components/corp/info/CorpInfoFormsConfirm";
import TopHeader from "components/top/TopHeader";
import { JOHOKU_ACCOUNT } from "enums";
import { usePrompt } from "hooks/usePrompt";
import { ApplyCorpModel, ContextModel, CorpInfoModel, IndustryMstModel, JohokuBranchMstModel, PreAccessTokenModel } from "models";
import qs from "query-string";
import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { Utils } from "utils";

/**
 * 会社情報登録申請画面 （ログイン前）
 */
const CorpEntryPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = qs.parse(location.search);
  const pre_entry_id = (query.pre_entry_id as string) ?? "";

  const ctx = useOutletContext<ContextModel>();
  const didEffect = React.useRef(false);

  const industryMst = React.useRef<IndustryMstModel[]>([]);
  const branchMst = React.useRef<JohokuBranchMstModel[]>([]);

  const [preAccess, setPreAccess] = React.useState<PreAccessTokenModel>();
  const [data, setData] = React.useState<ApplyCorpModel>({
    apply_mail_address: "",
    apply_corp_id: "",
    apply_user_id: "",
    apply_user_name: "",
    apply_password: "",
    apply_reenter_password: "",
  });
  const [info, setInfo] = React.useState<CorpInfoModel>(Utils.Model.createCorpInfoData());
  const [formIsDirty, setFormIsDirty] = React.useState(true);
  const [confirm, setConfirm] = React.useState(false);

  const onBeforeUnload = (e: BeforeUnloadEvent) => {
    console.log("onBefore");
    debugger;
    e.preventDefault();
    // ここに設定する文字列は表示には影響しない？
    e.returnValue = "check";
  };
  usePrompt("入力中の内容は破棄されます。よろしいですか？", formIsDirty, true);

  const onAddressSearch = async () => {
    ctx.loading.setState(true);
    try {
      const response = await API.Address.entryAddress(info.post_no1,info.post_no2, pre_entry_id);
      console.log(response);
      info.prefecture_cd = response.data.prefecture_cd ?? "";
      info.citytown = response.data.address ?? "";
      info.address = "";
      setInfo({ ...info });
    } catch (err) {
      Utils.errorAction(ctx, err);
    }
    ctx.loading.setState(false);
  };

  const onToConfirm = async () => {
    if (!data) {
      return;
    }

    if (info.invoice_no === "T") {
      info.invoice_no = "";
    }

    if (info.bank_account_list.length) {
      const is_JHK = info.bank_account_list.some((item) => item.bank_cd === JOHOKU_ACCOUNT.bank_cd);
      info.has_johoku_account = is_JHK;
    }

    ctx.loading.setState(true);
    try {
      // 法人個人区分が個人の場合は、法人番号をクリアする
      if (info.is_personal) {
        info.corp_no = "";
      }

      // 会社情報applyに詰め替え
      for (const k in info) {
        (data as any)["apply_" + k] = (info as any)[k];
      }
      (data as any).apply_bank_account_list = [];
      // 口座情報applyに詰め替え
      for (const bank_account_list of info.bank_account_list) {
        const tmp = {} as any;
        for (const k in bank_account_list) {
          tmp["apply_" + k] = (bank_account_list as any)[k];
        }
        (data as any).apply_bank_account_list.push(tmp);
      }

      const response = await API.ApplyCorp.preCheck(data, pre_entry_id);
      console.log(response);
      setConfirm(true);
      refreshBankAccount();
      window.scrollTo({
        top: 0,
        behavior: "auto",
      });
    } catch (err) {
      Utils.errorAction(ctx, err);
      handlerErrorBankAccount(err);
    }
    ctx.loading.setState(false);
  };

  const onNext = async () => {
    setFormIsDirty(false);

    if (info.invoice_no === "T") {
      info.invoice_no = "";
    }

    if (info.bank_account_list.length) {
      const is_JHK = info.bank_account_list.some((item) => item.bank_cd === JOHOKU_ACCOUNT.bank_cd);
      info.has_johoku_account = is_JHK;
    }

    ctx.loading.setState(true);
    try {
      // 会社情報applyに詰め替え
      for (const k in info) {
        (data as any)["apply_" + k] = (info as any)[k];
      }
      // 口座情報applyに詰め替え
      (data as any).apply_bank_account_list = [];
      for (const bank_account_list of info.bank_account_list) {
        const tmp = {} as any;
        for (const k in bank_account_list) {
          tmp["apply_" + k] = (bank_account_list as any)[k];
        }
        (data as any).apply_bank_account_list.push(tmp);
      }
      const response = await API.ApplyCorp.create(data, pre_entry_id);
      console.log(response);
      navigate("/company-entry-apply-finish/");
    } catch (err) {
      Utils.errorAction(ctx, err);
      handlerErrorBankAccount(err);
    }
    ctx.loading.setState(false);
  };

  const handlerErrorBankAccount = (errors: any) => {
    if(!errors) return;
    if (errors && errors.response?.status === 400 && !!info.bank_account_list.length) {
      let tmpBank = {} as any;

      info.bank_account_list.forEach((bank) => {
        let keyString = `${bank["account_type"]}|${bank["account_no"]}|${bank["branch_cd"]}|${bank["bank_cd"]}`;

        if (tmpBank[keyString]) {
          tmpBank[keyString].is_duplicate = true;
          bank.is_duplicate = true;
        } else {
          tmpBank[keyString] = bank;
          bank.is_duplicate = false;
        }
      });
      setInfo({ ...info });
    }
  };

  const refreshBankAccount = () => {
    if(!info) return;

    info.bank_account_list.forEach((bank) => bank.is_duplicate = false);
    setInfo({ ...info })
  };

  React.useEffect(() => {
    if (didEffect.current) {
      return;
    }
    didEffect.current = true;
    const f = async () => {
      ctx.loading.setState(false);
      try {
        {
          const response = await API.Auth.preAccessTokenCheck(pre_entry_id);
          console.log(response);
          const preAccess = response.data;
          setPreAccess(preAccess);
          data.apply_mail_address = preAccess.pre_mail_address;
          if (preAccess.invite_corp && preAccess.token_category === 1) {
            data.apply_user_name = preAccess.invite_corp.staff_name;
            info.corp_name = preAccess.invite_corp.corp_name;
            info.is_personal = preAccess.invite_corp.is_personal;
            info.corp_no = preAccess.invite_corp.corp_no;
          }
        }
        {
          const response = await API.IndustryMst.entryList(pre_entry_id);
          console.log(response);
          industryMst.current = response.data;
        }
        {
          const response = await API.JohokuBranchMst.entryList(pre_entry_id);
          console.log(response);
          branchMst.current = response.data;
        }

        Utils.Dummy.setDummyApplyCorpData(data);
        setData({ ...data });
        Utils.Dummy.setDummyCorpInfoData(info);
        setInfo({ ...info });
      } catch (err) {
        Utils.errorAction(ctx, err);
      }
      ctx.loading.setState(false);
    };
    f();
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!preAccess || !preAccess.token_category) {
    return <></>;
  }

  if (!confirm) {
    return (
      <div className="w-full">
        <Helmet>
          <title>会社登録申請</title>
        </Helmet>
        <TopHeader />

        <div className="px-16">
          <PageTitle title="会社登録申請" className="mb-4" />

          <ApplyCorpForms data={data} setData={setData} />
          <CorpInfoForms
            isUpdate={false}
            data={info}
            setData={setInfo}
            industryMst={industryMst.current}
            branchMst={branchMst.current}
            onAddressSearch={onAddressSearch}
          />
        </div>

        <div className="flex justify-center p-8">
          <button className="primary px-4 py-2" onClick={onToConfirm}>
            入力内容の確認
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <TopHeader />

      <div className="px-16">
        <PageTitle title="会社登録申請" className="border-b" />
        <div className="mt-4">以下の内容で会社情報の登録を申請します。</div>

        <div className="mt-4 flex flex-col gap-4">
          <div className="pt-4 border-b font-bold">ログイン情報登録</div>
          <div className="flex justify-between items-center">
            <div className="w-3/12">メールアドレス</div>
            <div className="w-9/12">{data.apply_mail_address}</div>
          </div>
          <div className="flex justify-between items-center">
            <div className="w-3/12">CORPID</div>
            <div className="w-9/12">{data.apply_corp_id}</div>
          </div>
          <div className="flex justify-between items-center">
            <div className="w-3/12">USERID</div>
            <div className="w-9/12">{data.apply_user_id}</div>
          </div>
          <div className="flex justify-between items-center">
            <div className="w-3/12">管理者の氏名</div>
            <div className="w-9/12">{data.apply_user_name}</div>
          </div>
          <div className="flex justify-between items-center">
            <div className="w-3/12">パスワード</div>
            <div className="w-9/12">入力したパスワード</div>
          </div>
        </div>

        <CorpInfoFormsConfirm data={info} industryMst={industryMst.current} isCreate={true} />
      </div>

      <div className="flex justify-center gap-4 p-8">
        <button className="primary px-4 py-2" onClick={onNext}>
          申請する
        </button>
        <button
          className="secondary px-4 py-2"
          onClick={() => {
            setConfirm(false);
          }}
        >
          戻る
        </button>
      </div>
    </div>
  );
};
export default CorpEntryPage;
